import {
  abortTempChatStream,
  isApiKeyAvailable,
} from "@/services/tempChatService";
import { fileUtils } from "@/lib/utils/fileUtils";
import useTempChatStore from "@/store/tempChatStore";
import PropTypes from "prop-types";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const TempChatContext = createContext(null);

/**
 * Provider component for temporary chat functionality
 */
export function TempChatProvider({ children }) {
  // Track API availability - check once on mount
  const [isApiAvailable, setIsApiAvailable] = useState(true);
  const [apiErrorType, setApiErrorType] = useState(null);

  useEffect(() => {
    // Check API availability once on mount with structured error handling
    isApiKeyAvailable()
      .then((result) => {
        setIsApiAvailable(result.available);
        // Store error type for better messaging
        if (!result.available) {
          setApiErrorType(result.errorType);
        }
      })
      .catch(() => {
        setIsApiAvailable(false);
        setApiErrorType("network");
      });
  }, []);

  // Get store state and actions
  const {
    messages,
    isStreaming,
    error,
    errorType,
    retryCount,
    maxRetries,
    sendMessage,
    clearChat,
    exportMessages,
    retryLastMessage,
  } = useTempChatStore();

  /**
   * Send a message in temp chat
   */
  const handleSendMessage = useCallback(
    async (content) => {
      if (!content?.trim()) return;

      if (!isApiAvailable) {
        console.error("Backend API not available for temp chat");

        // Add user message even when API is unavailable
        const userMessageId = fileUtils.generateUUID();
        const userMessage = {
          id: userMessageId,
          role: "user",
          content: content.trim(),
          timestamp: new Date().toISOString(),
        };
        useTempChatStore.getState().addMessage(userMessage);

        // Create error assistant message (Claude pattern)
        // Error appears as an assistant message bubble in the conversation
        const errorMessageId = fileUtils.generateUUID();
        const errorContent =
          apiErrorType === "network"
            ? "I wasn't able to connect. Please check your internet connection and try again."
            : "I'm having trouble responding right now. Please try again in a moment.";

        // Add empty error message first
        const assistantErrorMessage = {
          id: errorMessageId,
          role: "assistant",
          content: "", // Start empty, will stream in
          timestamp: new Date().toISOString(),
          isError: true,
          errorType: apiErrorType || "network",
          canRetry: true,
        };
        useTempChatStore.getState().addMessage(assistantErrorMessage);

        // Stream the error message in (simulates model response)
        useTempChatStore
          .getState()
          .streamTextIntoMessage(errorMessageId, errorContent);
        return;
      }

      try {
        await sendMessage(content);
      } catch (error) {
        console.error("Error sending temp chat message:", error);
        // Error is already handled in the store
      }
    },
    [sendMessage, isApiAvailable, apiErrorType]
  );

  /**
   * Abort the current streaming response
   */
  const handleAbortStream = useCallback(() => {
    return abortTempChatStream();
  }, []);

  /**
   * Clear the current chat session
   */
  const handleClearChat = useCallback(() => {
    clearChat();
  }, [clearChat]);

  /**
   * Export messages for conversion to real chat
   */
  const handleExportMessages = useCallback(() => {
    return exportMessages();
  }, [exportMessages]);

  /**
   * Convert temp chat to real chat
   * Exports messages to sessionStorage and navigates to chat
   */
  const convertToRealChat = useCallback(() => {
    const messagesToExport = exportMessages();

    if (messagesToExport.length === 0) {
      console.warn("No messages to export for conversion to real chat");
      return false;
    }

    // Store messages in sessionStorage for pickup by real chat
    sessionStorage.setItem(
      "tempChatConversion",
      JSON.stringify({
        messages: messagesToExport,
        timestamp: Date.now(),
        convertedFrom: "tempChat",
      })
    );

    return true;
  }, [exportMessages]);

  const contextValue = {
    // State
    messages,
    isStreaming,
    error,
    errorType,
    retryCount,
    maxRetries,

    // Actions
    sendMessage: handleSendMessage,
    abortStream: handleAbortStream,
    clearChat: handleClearChat,
    exportMessages: handleExportMessages,
    convertToRealChat,
    retryLastMessage, // Retry from error message (Claude pattern)

    // Utility
    hasMessages: messages.length > 0,
    canSend: !isStreaming && isApiAvailable,
  };

  return (
    <TempChatContext.Provider value={contextValue}>
      {children}
    </TempChatContext.Provider>
  );
}

TempChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to use temp chat context
 */
export function useTempChat() {
  const context = useContext(TempChatContext);
  if (!context) {
    // Return null when not in a TempChatProvider (safe fallback)
    return null;
  }
  return context;
}

export default TempChatContext;
